import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, MapPin, Send, Check } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';

const ContactPage = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Contact Us - Get a Free Quote"
        description="Contact JPR Customs in Murray Bridge SA for a free quote on spray painting, crash repairs, panel beating, car restoration & more. Call +61 417 119 544 or email admin@jprcustoms.com.au. 13 Hindmarsh Rd, Murray Bridge."
        keywords="contact JPR Customs, free quote, Murray Bridge auto body, car spray painting quote, crash repairs quote"
        canonical="/contact"
      />
      <Header />
      <main className="pt-44 pb-20">
        <div className="max-w-6xl mx-auto px-4">
          {/* Page Title */}
          <div className="text-center mb-16">
            <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Contact Us</h1>
            <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>
              Ready to transform your vehicle? Get in touch with our team for a free consultation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className={`p-8 md:p-10 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
              <h2 className="text-2xl font-bold text-lime-500 mb-6">Send Us a Message</h2>
              
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mb-4">
                    <Check className="w-8 h-8 text-black" />
                  </div>
                  <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Message Sent!</h3>
                  <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>We'll get back to you as soon as possible.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none transition-colors ${isDark ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500' : 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400'}`}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none transition-colors ${isDark ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500' : 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400'}`}
                        placeholder="your@email.com"
                      />
                    </div>
                    <div>
                      <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none transition-colors ${isDark ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500' : 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400'}`}
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Service Required</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none transition-colors ${isDark ? 'bg-neutral-800 border-neutral-700 text-white' : 'bg-white border-neutral-300 text-neutral-900'}`}
                    >
                      <option value="">Select a service</option>
                      <option value="restoration">Vintage & Classic Restoration</option>
                      <option value="crash">Crash Repairs</option>
                      <option value="accessories">Vehicle Accessories</option>
                      <option value="paint">Paint & Custom Work</option>
                      <option value="boat">Ski Boat & RV Resprays</option>
                      <option value="towing">Towing Services</option>
                      <option value="mechanical">Mechanical Repairs</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className={`block mb-2 text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className={`w-full px-4 py-3 rounded-lg border focus:border-lime-500 focus:outline-none transition-colors resize-none ${isDark ? 'bg-neutral-800 border-neutral-700 text-white placeholder-neutral-500' : 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-400'}`}
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold rounded-lg hover:bg-lime-400 transition-all duration-300"
                  >
                    <Send className="w-5 h-5" />
                    Send Message
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
                <h2 className="text-2xl font-bold text-lime-500 mb-6">Contact Information</h2>
                <ul className="space-y-6">
                  <li>
                    <a 
                      href={`https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-start gap-4 transition-colors group ${isDark ? 'text-neutral-300 hover:text-lime-400' : 'text-neutral-700 hover:text-lime-600'}`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isDark ? 'bg-neutral-800 group-hover:bg-lime-500 group-hover:text-black' : 'bg-neutral-200 group-hover:bg-lime-500 group-hover:text-black'}`}>
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Visit Us</h4>
                        <p>{companyInfo.address}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a 
                      href={`tel:${companyInfo.phone}`}
                      className={`flex items-start gap-4 transition-colors group ${isDark ? 'text-neutral-300 hover:text-lime-400' : 'text-neutral-700 hover:text-lime-600'}`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isDark ? 'bg-neutral-800 group-hover:bg-lime-500 group-hover:text-black' : 'bg-neutral-200 group-hover:bg-lime-500 group-hover:text-black'}`}>
                        <Phone className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Call Us</h4>
                        <p>{companyInfo.phone}</p>
                      </div>
                    </a>
                  </li>
                  <li>
                    <a 
                      href={`mailto:${companyInfo.email}`}
                      className={`flex items-start gap-4 transition-colors group ${isDark ? 'text-neutral-300 hover:text-lime-400' : 'text-neutral-700 hover:text-lime-600'}`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${isDark ? 'bg-neutral-800 group-hover:bg-lime-500 group-hover:text-black' : 'bg-neutral-200 group-hover:bg-lime-500 group-hover:text-black'}`}>
                        <Mail className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className={`font-semibold mb-1 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Email Us</h4>
                        <p>{companyInfo.email}</p>
                      </div>
                    </a>
                  </li>
                </ul>
              </div>

              <div className={`p-8 rounded-xl ${isDark ? 'bg-neutral-900' : 'bg-neutral-50'}`}>
                <h2 className="text-2xl font-bold text-lime-500 mb-6">Hours of Operation</h2>
                <ul className="space-y-3">
                  <li className={`flex justify-between py-2 border-b ${isDark ? 'text-neutral-300 border-neutral-800' : 'text-neutral-600 border-neutral-200'}`}>
                    <span>Monday - Friday</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-neutral-900'}`}>{companyInfo.hours.weekdays}</span>
                  </li>
                  <li className={`flex justify-between py-2 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                    <span>Saturday - Sunday</span>
                    <span className={`font-medium ${isDark ? 'text-white' : 'text-neutral-900'}`}>{companyInfo.hours.weekend}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
